import { getToken } from '@/core/token';

/**
 * Central-login → custom-domain handoff: exchange the current session for a
 * one-time code and bounce the browser back to `returnTo` (a custom-domain
 * URL) so it can redeem the code and enter the app already logged in.
 *
 * Shared by:
 * - `LoginForm`'s post-login flow (passkey / OTP / password / 2FA success).
 * - `LoginPage`'s mount-time fast path for already-authenticated users
 *   deep-linking from a custom domain (see the "Login handoff" section of
 *   docs/superpowers/specs/2026-07-17-custom-domains-design.md).
 *
 * Returns `false` (never throws) on any rejection — invalid/non-https
 * `returnTo`, expired/missing session, the user not being a member of the
 * org that owns the domain, or a network error — so callers can fall
 * through to the normal login form.
 */
export const attemptHandoff = async (returnTo: string): Promise<boolean> => {
  try {
    const target = new URL(returnTo);
    if (target.protocol !== 'https:') return false;
    const res = await fetch(`${import.meta.env.VITE_ENDPOINT}/custom-domain/handoff`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ returnTo }),
    });
    if (!res.ok) {
      // Fallback to normal navigation is silent for the user — leave a trace
      // for debugging (e.g. UNKNOWN_DOMAIN / NOT_A_MEMBER from the server).
      const body = await res.json().catch(() => null);
      console.warn('[handoff] rejected', res.status, body?.error);
      return false;
    }
    const { code } = await res.json();
    if (!code) return false;
    const path = encodeURIComponent(target.pathname + target.search);
    window.location.href = `${target.origin}/auth/handoff?code=${encodeURIComponent(code)}&path=${path}`;
    return true;
  } catch {
    return false;
  }
};
